import {
  addMetaDataForCustomFields,
  deleteMetaDataFields,
  getAllMetaDataForCustomFields,
  reshuffleCustomFields,
  updateCustomFields
} from 'API/fieldManagementApi';
import { showAlertMessage } from 'Features/alertMessage/alertMessageSlice';
import { getDeleteObject, getShuffleObject, getUpdateObject } from 'Helpers/areaActions';
import { tWithErrorFallback, tWithSuccessFallback } from 'Hooks/useTranslationWithFallback';
import { snakeCase, startCase, toLower } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

const useApiActionHandler = (dispatch) => {
  const handleApiCall = async (actionFn, data, successKey, errorKey) => {
    try {
      const response = await dispatch(actionFn(data)).unwrap();
      dispatch(
        showAlertMessage({
          type: 'success',
          text: tWithSuccessFallback(`apiSuccessMessages.${response?.messageKey || successKey}`)
        })
      );
    } catch (error) {
      const messageKey = error?.messageKey || error?.message || errorKey;
      dispatch(
        showAlertMessage({
          type: 'error',
          text: tWithErrorFallback(`apiErrorMessages.${messageKey}`)
        })
      );
    }
  };

  return { handleApiCall };
};

export const useFieldManagement = () => {
  const dispatch = useDispatch();
  const {
    getManageFieldsStatus,
    addMetaDataFieldStatus,
    updateCustomFieldStatus,
    deleteMetaDataFieldStatus,
    reshuffleCustomFieldStatus,
    getManageFieldsData
  } = useSelector((state) => state.fieldManagement);

  const methods = useForm();
  const { reset, control } = methods;
  const watchedValues = useWatch({ control });

  const {
    plannerEmail,
    division,
    meetingType,
    meetingTypeSelected,
    segment,
    segmentSelected,
    area,
    areaSelected,
    districtSelected,
    dentalEducationMethod,
    dentalClinicalCaseType
  } = watchedValues;

  const formatLabel = (input) => {
    return startCase(toLower(input));
  };

  const segmentOptions = useMemo(() => {
    return segment?.map((item) => ({
      ...item,
      value: formatLabel(item.value),
      key: snakeCase(item.key)
    }));
  }, [segment]);

  const { handleApiCall } = useApiActionHandler(dispatch);
  const initialLoadTriggered = useRef(false);

  useEffect(() => {
    if (!initialLoadTriggered.current && getManageFieldsStatus === 'idle') {
      initialLoadTriggered.current = true;
      dispatch(getAllMetaDataForCustomFields());
    }
  }, [dispatch, getManageFieldsStatus]);

  useEffect(() => {
    if (
      addMetaDataFieldStatus === 'succeeded' ||
      updateCustomFieldStatus === 'succeeded' ||
      deleteMetaDataFieldStatus === 'succeeded' ||
      reshuffleCustomFieldStatus === 'succeeded'
    ) {
      dispatch(getAllMetaDataForCustomFields());
    }
  }, [
    addMetaDataFieldStatus,
    updateCustomFieldStatus,
    deleteMetaDataFieldStatus,
    reshuffleCustomFieldStatus,
    dispatch
  ]);

  useEffect(() => {
    reset({ ...getManageFieldsData });
  }, [reset, getManageFieldsData]);

  const handleAction = async (name, item, parent, grandParent, action) => {
    const isDelete = action === 'delete';
    const data = isDelete
      ? getDeleteObject(name, item, parent, grandParent)
      : getUpdateObject(name, item, parent, grandParent, action);

    const apiMap = {
      create: addMetaDataForCustomFields,
      update: updateCustomFields,
      delete: deleteMetaDataFields
    };

    const apiAction = apiMap[action];

    if (!apiAction) return;

    const payload = action === 'delete' ? { payload: data } : data;

    await handleApiCall(apiAction, payload);
  };

  const handleShuffle = async (name, shuffleObject, selectedGrandParent) => {
    const data = getShuffleObject(name, shuffleObject, selectedGrandParent);
    await handleApiCall(reshuffleCustomFields, data);
  };

  const dropdownConfigs = [
    {
      name: 'plannerEmail',
      title: 'Planner Email',
      options: plannerEmail,
      currentLevel: 0,
      isEditable: true
    },
    { name: 'division', title: 'Division', options: division, currentLevel: 0, isEditable: true },
    { name: 'area', title: 'Area', options: area, currentLevel: 0, isEditable: true },
    {
      name: 'district',
      title: 'District',
      options: areaSelected?.level0 || [],
      parentOptions: area,
      selectedParent: areaSelected,
      currentLevel: 1,
      isEditable: true
    },
    {
      name: 'region',
      title: 'Region',
      options: districtSelected?.level1 || [],
      parentOptions: areaSelected?.level0 || [],
      selectedParent: districtSelected,
      selectedGrandParent: areaSelected,
      currentLevel: 2,
      isEditable: true
    },
    {
      name: 'meetingType',
      title: 'Meeting Type',
      options: meetingType,
      currentLevel: 0,
      isEditable: false
    },
    {
      name: 'meetingSubType',
      title: 'Meeting Sub Type',
      options: meetingTypeSelected?.level0 || [],
      parentOptions: meetingType,
      selectedParent: meetingTypeSelected,
      extraDropdownOptions: [
        { value: 'Online', name: 'Online' },
        { value: 'On-demand', name: 'On-demand' },
        { value: 'In-person', name: 'In-person' }
      ],
      currentLevel: 1,
      isEditable: false
    },
    {
      name: 'segment',
      title: 'Segment',
      options: segmentOptions,
      currentLevel: 0,
      isEditable: true
    },
    {
      name: 'product',
      title: 'Product',
      options: segmentSelected?.level0 || [],
      parentOptions: segment,
      selectedParent: segmentSelected,
      currentLevel: 1,
      isEditable: true
    },
    {
      name: 'dentalEducationMethod',
      title: 'Dental - Education Method',
      options: dentalEducationMethod,
      currentLevel: 0,
      isEditable: true
    },
    {
      name: 'dentalClinicalCaseType',
      title: 'Dental Clinical Case Type/Procedure',
      options: dentalClinicalCaseType,
      currentLevel: 0,
      isEditable: true
    }
  ];

  return {
    methods,
    dropdownConfigs,
    handleAction,
    handleShuffle
  };
};
